import React, { useEffect, useRef, useState } from "react";
import Markdown from "./Markdown";
import DataTable from "./DataTable";
import {
  AlertIcon, CalendarIcon, CheckIcon, ChevronIcon, CopyIcon, DownloadIcon, EditIcon, InfoIcon, PrintIcon,
  RefreshIcon, SparkIcon, ThumbDownIcon, ThumbUpIcon,
} from "./Icons";
import { copyText, formatTime, prettyHeader, rangeLabel } from "../utils";

const TOOL_LABELS = {
  query_records: "Queried records",
  pipeline_snapshot: "Tab counts",
  stage_movement_report: "Stage movements",
  tender_details: "Tender details",
  employee_report: "Employee report",
  team_summary: "Team summary",
  emd_report: "EMD / BG report",
  audit_activity: "Audit logs",
  distinct_values: "Looked up values",
  find_employee: "Found employee",
  describe_fields: "Read fields",
  stage_durations: "Stage durations",
  bidder_analysis: "Competitor analysis",
};

function UserMessage({ msg, onEdit }) {
  return (
    <div className="bdai-msg bdai-msg-user">
      <div className="bdai-bubble bdai-bubble-user">
        <div className="bdai-user-text">{msg.content}</div>
        {msg.range && (msg.range.from || msg.range.to) && (
          <div className="bdai-user-range">
            <CalendarIcon size={11} /> {rangeLabel(msg.range)}
          </div>
        )}
      </div>
      <div className="bdai-msg-meta bdai-msg-meta-user">
        <span>{formatTime(msg.createdAt)}</span>
        {onEdit && (
          <button type="button" className="bdai-link-btn" onClick={() => onEdit(msg)} title="Edit and ask again">
            <EditIcon size={11} /> Edit
          </button>
        )}
      </div>
    </div>
  );
}

function Details({ msg }) {
  const [open, setOpen] = useState(false);
  const tools = msg.toolsUsed || [];
  if (!tools.length && !msg.durationMs) return null;
  const seconds = msg.durationMs ? (msg.durationMs / 1000).toFixed(1) : null;
  return (
    <div className="bdai-details">
      <button type="button" className="bdai-details-toggle" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        <InfoIcon size={12} />
        <span>
          {tools.length ? `Used ${tools.length} data ${tools.length === 1 ? "query" : "queries"}` : "Details"}
          {seconds ? ` · ${seconds}s` : ""}
        </span>
        <ChevronIcon size={12} className={open ? "bdai-rot" : ""} />
      </button>
      {open && (
        <div className="bdai-details-body">
          {tools.map((t, i) => (
            <div key={i} className={`bdai-tool ${t.ok === false ? "bdai-tool-fail" : ""}`}>
              <span className="bdai-tool-name">{TOOL_LABELS[t.tool] || prettyHeader(t.tool)}</span>
              <span className="bdai-tool-args">
                {Object.entries(t.args || {})
                  .filter(([, v]) => v !== null && v !== "" && !(Array.isArray(v) && !v.length))
                  .map(([k, v]) => `${k}: ${typeof v === "object" ? JSON.stringify(v) : v}`)
                  .join(" · ")}
              </span>
              <span className="bdai-tool-ms">{t.ok === false ? "retried" : `${t.ms ?? "–"} ms`}</span>
            </div>
          ))}
          {msg.usage && msg.usage.total_tokens ? (
            <div className="bdai-details-foot">Tokens: {msg.usage.total_tokens.toLocaleString("en-IN")}</div>
          ) : null}
          {(msg.notes || []).map((n, i) => (
            <div key={i} className="bdai-details-note">⚠ {n}</div>
          ))}
        </div>
      )}
    </div>
  );
}

function AssistantMessage({ msg, isLast, onRegenerate, onFeedback, onExport, onExpandTable, onRetry, assistantName }) {
  const [copied, setCopied] = useState(false);
  const [exporting, setExporting] = useState(null);
  const [exportOpen, setExportOpen] = useState(false);
  const exportRef = useRef(null);
  const tables = msg.tables || [];

  useEffect(() => {
    if (!exportOpen) return undefined;
    const close = (e) => exportRef.current && !exportRef.current.contains(e.target) && setExportOpen(false);
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [exportOpen]);

  if (msg.stopped) {
    return (
      <div className="bdai-msg bdai-msg-bot">
        <div className="bdai-stopped">
          <span>You stopped this answer.</span>
          {onRetry && (
            <button type="button" className="bdai-link-btn bdai-link-btn-on" onClick={() => onRetry(msg)}>
              <RefreshIcon size={12} /> Ask again
            </button>
          )}
        </div>
      </div>
    );
  }

  if (msg.error) {
    return (
      <div className="bdai-msg bdai-msg-bot">
        <div className="bdai-avatar bdai-avatar-error"><AlertIcon size={15} /></div>
        <div className="bdai-bot-col">
          <div className="bdai-bubble bdai-bubble-error">
            <div className="bdai-error-title">Couldn't get an answer</div>
            <div className="bdai-error-text">{msg.error}</div>
            {onRetry && (
              <button type="button" className="bdai-btn bdai-btn-outline bdai-btn-sm" onClick={() => onRetry(msg)}>
                <RefreshIcon size={13} /> Try again
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  const handleCopy = async () => {
    if (await copyText(msg.content)) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };

  const handleExport = async (type) => {
    setExportOpen(false);
    setExporting(type);
    try {
      await onExport(msg, type);
    } finally {
      setExporting(null);
    }
  };

  const handlePrint = () => {
    const w = window.open("", "_blank", "width=900,height=700");
    if (!w) return;
    const node = document.getElementById(`bdai-answer-${msg.id}`);
    w.document.write(
      `<html><head><title>${assistantName} report</title><style>
        body{font-family:-apple-system,Segoe UI,Roboto,sans-serif;color:#1f2937;padding:24px;font-size:13px;line-height:1.55}
        table{border-collapse:collapse;width:100%;margin:10px 0}th,td{border:1px solid #e5e7eb;padding:6px 8px;text-align:left}
        th{background:#f3f6fa}h2,h3,h4{color:#1e5a96}button,select,input,.bdai-table-tools,.bdai-pager,.bdai-table-search{display:none!important}
      </style></head><body>${node ? node.innerHTML : ""}</body></html>`
    );
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 300);
  };

  return (
    <div className="bdai-msg bdai-msg-bot">
      <div className="bdai-avatar"><SparkIcon size={15} /></div>
      <div className="bdai-bot-col">
        <div className="bdai-bubble bdai-bubble-bot" id={`bdai-answer-${msg.id}`}>
          <Markdown text={msg.content} />
          {tables.length > 0 && (
            <div className="bdai-tables">
              {tables.map((t, i) => (
                <DataTable
                  key={`${msg.id}-${i}`}
                  title={t.title}
                  rows={t.rows || []}
                  defaultOpen={i < 2}
                  onExpand={() => onExpandTable(t)}
                />
              ))}
            </div>
          )}
        </div>
        <Details msg={msg} />
        <div className="bdai-actions">
          <span className="bdai-msg-time">{formatTime(msg.createdAt)}</span>
          <button type="button" className="bdai-icon-btn bdai-icon-btn-xs" onClick={handleCopy} title="Copy answer">
            {copied ? <CheckIcon size={13} /> : <CopyIcon size={13} />}
          </button>
          {msg.serverId && (
            <>
              <button
                type="button"
                className={`bdai-icon-btn bdai-icon-btn-xs ${msg.feedback === 1 ? "bdai-on-good" : ""}`}
                onClick={() => onFeedback(msg, msg.feedback === 1 ? 0 : 1)}
                title="Helpful"
                aria-pressed={msg.feedback === 1}
              >
                <ThumbUpIcon size={13} />
              </button>
              <button
                type="button"
                className={`bdai-icon-btn bdai-icon-btn-xs ${msg.feedback === -1 ? "bdai-on-bad" : ""}`}
                onClick={() => onFeedback(msg, msg.feedback === -1 ? 0 : -1)}
                title="Not helpful"
                aria-pressed={msg.feedback === -1}
              >
                <ThumbDownIcon size={13} />
              </button>
            </>
          )}
          {isLast && onRegenerate && (
            <button type="button" className="bdai-icon-btn bdai-icon-btn-xs" onClick={() => onRegenerate(msg)} title="Regenerate answer">
              <RefreshIcon size={13} />
            </button>
          )}
          <button type="button" className="bdai-icon-btn bdai-icon-btn-xs" onClick={handlePrint} title="Print / save as PDF">
            <PrintIcon size={13} />
          </button>
          {msg.serverId && tables.length > 0 && (
            <div className="bdai-export" ref={exportRef}>
              <button
                type="button"
                className="bdai-btn bdai-btn-outline bdai-btn-xs"
                onClick={() => setExportOpen((o) => !o)}
                disabled={!!exporting}
                aria-haspopup="menu"
                aria-expanded={exportOpen}
              >
                <DownloadIcon size={12} /> {exporting ? "Preparing…" : "Export"}
              </button>
              {exportOpen && (
                <div className="bdai-export-menu" role="menu">
                  <button type="button" role="menuitem" onClick={() => handleExport("xlsx")}>Excel (.xlsx)</button>
                  <button type="button" role="menuitem" onClick={() => handleExport("csv")}>CSV (.csv)</button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Message(props) {
  return props.msg.role === "user" ? <UserMessage msg={props.msg} onEdit={props.onEdit} /> : <AssistantMessage {...props} />;
}
